/**
 * The management table.
 *
 * Desktop-first on purpose: this is the surface for working through two hundred
 * recipes, and density is the feature. Status, favourite and categories are
 * editable in place because the alternative — open, edit, save, go back — is
 * four actions for a change that should be one.
 *
 * On a phone the same markup becomes cards (see index.css). A 900px table in a
 * 390px viewport is a horizontal scroll nobody uses.
 */
import { useMemo, useState } from 'react'
import Icon from '../Icon'
import { CategoryChips, Empty } from './Bits'
import {
  STATUS_LABEL, deleteRecipe, updateRecipe,
  type Category, type RecipeRow, type Status,
} from '../../lib/savenmake'

type SortKey = 'title' | 'status' | 'updatedAt' | 'timesCooked' | 'totalMinutes'

export default function LibraryTable({ recipes, categories, onChanged, onOpen, onCook }: {
  recipes: RecipeRow[]
  categories: Category[]
  onChanged: () => void
  onOpen: (r: RecipeRow) => void
  onCook: (r: RecipeRow) => void
}) {
  const [search, setSearch] = useState('')
  const [kindFilter, setKindFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState<Status | ''>('')
  const [showSamples, setShowSamples] = useState(true)
  const [sort, setSort] = useState<SortKey>('updatedAt')
  const [desc, setDesc] = useState(true)

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase()
    let out = recipes.filter((r) => {
      if (!showSamples && r.isSample) return false
      if (statusFilter && r.status !== statusFilter) return false
      if (kindFilter && !r.categories.some((c) => c.id === kindFilter)) return false
      if (!q) return true
      return r.title.toLowerCase().includes(q)
        || (r.sourceCreator ?? '').toLowerCase().includes(q)
        || r.categories.some((c) => c.name.toLowerCase().includes(q))
    })
    out = [...out].sort((a, b) => {
      const pick = (r: RecipeRow) => {
        switch (sort) {
          case 'title': return r.title.toLowerCase()
          case 'status': return r.status
          case 'timesCooked': return r.timesCooked + (r.priorMakes ?? 0)
          case 'totalMinutes': return r.totalMinutes ?? Number.MAX_SAFE_INTEGER
          default: return r.updatedAt
        }
      }
      const x = pick(a); const y = pick(b)
      const cmp = typeof x === 'string' && typeof y === 'string' ? x.localeCompare(y) : Number(x) - Number(y)
      return desc ? -cmp : cmp
    })
    return out
  }, [recipes, search, kindFilter, statusFilter, showSamples, sort, desc])

  const head = (key: SortKey, label: string) => (
    <th scope="col">
      <button type="button" onClick={() => { if (sort === key) setDesc(!desc); else { setSort(key); setDesc(true) } }}>
        {label}{sort === key ? (desc ? ' ↓' : ' ↑') : ''}
      </button>
    </th>
  )

  const set = async (r: RecipeRow, patch: Record<string, unknown>) => {
    await updateRecipe(r.id, patch)
    onChanged()
  }

  return (
    <>
      <div className="filters">
        <div className="filter-row">
          <input
            className="grow" type="search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search the library…" aria-label="Search"
          />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as Status | '')} style={{ maxWidth: 170 }} aria-label="Filter by status">
            <option value="">Any status</option>
            {(Object.keys(STATUS_LABEL) as Status[]).map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
          </select>
          <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} style={{ maxWidth: 200 }} aria-label="Filter by category">
            <option value="">Any category</option>
            {categories.filter((c) => c.count > 0).map((c) => (
              <option key={c.id} value={c.id}>{c.name} ({c.count})</option>
            ))}
          </select>
          <label className="toggle">
            <input type="checkbox" checked={showSamples} onChange={(e) => setShowSamples(e.target.checked)} />
            Show samples
          </label>
        </div>
        <p className="muted small" style={{ margin: 0 }}>{rows.length} of {recipes.length} recipes</p>
      </div>

      {!rows.length ? (
        <Empty icon="book" title={recipes.length ? 'Nothing matches that' : 'The library is empty'}>
          <p>{recipes.length ? 'Try a different search or clear the filters.' : 'Save a link, paste a caption, or add one by hand to get going.'}</p>
        </Empty>
      ) : (
        <div className="table-wrap">
          <table className="library">
            <thead>
              <tr>
                <th scope="col"><span className="sr-only">Favourite</span></th>
                {head('title', 'Recipe')}
                {head('status', 'Status')}
                <th scope="col">Categories</th>
                {head('totalMinutes', 'Time')}
                <th scope="col">Serves</th>
                {head('timesCooked', 'Made')}
                <th scope="col">Source</th>
                <th scope="col"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td data-label="Favourite">
                    <button
                      type="button" className="fav" style={{ position: 'static' }}
                      aria-pressed={r.favorite}
                      aria-label={r.favorite ? `Unfavourite ${r.title}` : `Favourite ${r.title}`}
                      onClick={() => void set(r, { favorite: !r.favorite })}
                    >
                      <Icon name="heart" size={16} />
                    </button>
                  </td>
                  <td className="title" data-label="Recipe">
                    <button type="button" className="link" onClick={() => onOpen(r)} style={{ textAlign: 'left' }}>{r.title}</button>
                    {r.isSample && <span className="chip is-kind" style={{ marginLeft: 6, fontSize: '0.68rem' }}>Sample</span>}
                    {r.openFlags > 0 && (
                      <div className="prov prov-suggested" style={{ marginTop: 4 }}>
                        <Icon name="alert" size={12} /> {r.openFlags} to check
                      </div>
                    )}
                  </td>
                  <td data-label="Status">
                    <select value={r.status} onChange={(e) => void set(r, { status: e.target.value })} aria-label={`Status of ${r.title}`}>
                      {(Object.keys(STATUS_LABEL) as Status[]).map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                    </select>
                  </td>
                  <td data-label="Categories" style={{ maxWidth: 260 }}>
                    <CategoryChips categories={r.categories} />
                  </td>
                  <td data-label="Time">{r.totalMinutes ? `${r.totalMinutes}m` : <span className="muted">—</span>}</td>
                  <td data-label="Serves">{r.servings ?? <span className="muted">—</span>}</td>
                  <td data-label="Made">
                    {r.timesCooked > 0 && <b>{r.timesCooked}×</b>}
                    {/* An estimate is labelled as one, always. It is not a count
                        of dated runs and must never read like one. */}
                    {!r.timesCooked && (r.priorMakes ?? 0) > 0 && <span className="muted">~{r.priorMakes}× before</span>}
                    {!r.timesCooked && !r.priorMakes && <span className="muted">—</span>}
                  </td>
                  <td data-label="Source">
                    {r.sourceUrl
                      ? <a href={r.sourceUrl} target="_blank" rel="noopener noreferrer">{r.sourcePlatform ?? 'link'}</a>
                      : <span className="muted">by hand</span>}
                  </td>
                  <td data-label="">
                    <div className="cell-actions">
                      <button type="button" onClick={() => onCook(r)} title="Begin"><Icon name="flame" size={15} /></button>
                      <button type="button" onClick={() => onOpen(r)} title="Edit"><Icon name="edit" size={15} /></button>
                      <button
                        type="button" title="Delete"
                        onClick={async () => {
                          if (!confirm(`Delete "${r.title}"? Its cooking history goes too.`)) return
                          await deleteRecipe(r.id)
                          onChanged()
                        }}
                      >
                        <Icon name="trash" size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
